import React, {useEffect, useState} from 'react';
import {createRefetchContainer, RelayRefetchProp} from 'react-relay';
import {fetchQuery, graphql} from 'relay-runtime';
import styled from 'styled-components';

import RestoreTaskMutation from '../../../mutations/mission_control/RestoreTaskMutation.js';
import Util from '../../../forecast-app/shared/util/util';
import MissionControlHeader from './MissionControlHeader';
import {RestoreData_viewer} from './__generated__/RestoreData_viewer.graphql';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';

import {BUTTON_COLOR} from '../../../constants';
import {hideLoader, showLoader} from '../../global_loader';
import environment from '../../../RelayEnvironment';
import {RestoreData_deletedTaskLevelInfo_QueryResponse} from './__generated__/RestoreData_deletedTaskLevelInfo_Query.graphql';

const Controls = styled.div`
	display: flex;
	gap: 8px;
	margin: 8px;
	input {
		width: 75px;
	}
`;

const deletedTaskLevelInfoQuery = graphql`
	query RestoreData_deletedTaskLevelInfo_Query($companyId: Int, $taskEventCount: ID!) {
		viewer {
			missionControlData {
				deletedTaskLevelInfo(companyId: $companyId, taskEventCount: $taskEventCount) {
					parentCompanyTaskId
					grandParentCompanyTaskId
				}
			}
		}
	}
`;

type WarningInformation = {
	text: string;
	taskHierarchy: boolean;
};

function fetchDeletedTasks(
	relay: RelayRefetchProp,
	companyId: number,
	page: number,
	daysAgo: number,
	setLoading: (value: React.SetStateAction<boolean>) => void
): void {
	setLoading(true);
	relay.refetch({companyId, page, daysAgo}, null, () => setLoading(false));
}

interface RestoreDataProps {
	viewer: RestoreData_viewer;
	relay: RelayRefetchProp;
}

function RestoreData({viewer, relay}: RestoreDataProps): JSX.Element {
	const [page, setPage] = useState(1);
	const [companyId, setCompanyId] = useState('');
	const [daysAgo, setDaysAgo] = useState('');
	const [restoring, setRestoring] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (companyId && daysAgo) {
			fetchDeletedTasks(relay, Number(companyId), page, Number(daysAgo), setLoading);
		}
	}, [page]);

	function getWarningInfo(data: RestoreData_deletedTaskLevelInfo_QueryResponse, taskName: string): WarningInformation {
		const deletedTaskLevelInfo = data?.viewer?.missionControlData?.deletedTaskLevelInfo;

		if (!deletedTaskLevelInfo) {
			return {text: `Are you sure you want to restore "${taskName}"?`, taskHierarchy: false};
		} else {
			if (deletedTaskLevelInfo.grandParentCompanyTaskId) {
				return {
					text: `This task has a parent task: "T${deletedTaskLevelInfo.parentCompanyTaskId}" and a grandparent task: "T${deletedTaskLevelInfo.grandParentCompanyTaskId}". Restoring this task: "${taskName}" without restoring the parent task and grandparent task first, will cause the task to be restored without its task hierarchy. Are you sure you want to restore "${taskName}"?`,
					taskHierarchy: true,
				};
			}
			if (deletedTaskLevelInfo.parentCompanyTaskId) {
				return {
					text: `This task has a parent task: "T${deletedTaskLevelInfo.parentCompanyTaskId}". Restoring this task: "${taskName}" without restoring the parent task first, will cause the task to be restored without its task hierarchy. Are you sure you want to restore "${taskName}"?`,
					taskHierarchy: true,
				};
			}
			return {
				text: `Are you sure you want to restore "${taskName}"?`,
				taskHierarchy: false,
			};
		}
	}

	function restoreTask(taskEventCount: string | undefined, taskName: string): void {
		if (!taskEventCount) {
			return;
		}

		fetchQuery(environment.getInstanceMC(), deletedTaskLevelInfoQuery, {
			companyId: Number(companyId),
			taskEventCount,
		}).then((response: RestoreData_deletedTaskLevelInfo_QueryResponse) => {
			const {text, taskHierarchy} = getWarningInfo(response, taskName);
			const callback = () => {
				if (restoring) {
					return;
				}
				showLoader();
				setRestoring(true);
				Util.CommitMutation(
					RestoreTaskMutation,
					{
						taskEventCount,
						ignoreParent: taskHierarchy,
					},
					() => {
						hideLoader();
						setRestoring(false);
						fetchDeletedTasks(relay, Number(companyId), page, Number(daysAgo), setLoading);
					}
				);
			};

			showModal({
				type: MODAL_TYPE.WARNING,
				message: 'Are you sure?',
				irreversible: true,
				warningInformation: [text],
				buttons: [
					{
						text: 'Cancel',
						color: BUTTON_COLOR.WHITE,
					},
					{
						text: taskHierarchy ? 'Restore without hierarchy' : 'Restore',
						callback,
					},
				],
			});
		});
	}

	const data = viewer.missionControlData?.paginatedDeletedTaskList;
	return (
		<div className="mission-control-list">
			<MissionControlHeader viewer={viewer} />
			<h1>Restore Data</h1>
			<Controls>
				<div>Company Id:</div>
				<input
					value={companyId}
					onChange={e => {
						setCompanyId(e.target.value.replace(/\D/g, ''));
					}}
				></input>
				<div>Last</div>
				<input
					value={daysAgo}
					onChange={e => {
						setDaysAgo(e.target.value.replace(/\D/g, ''));
					}}
				/>
				<div>days</div>
				<button
					onClick={() => {
						if (page !== 1) {
							setPage(1);
						} else {
							fetchDeletedTasks(relay, Number(companyId), page, Number(daysAgo), setLoading);
						}
					}}
					disabled={!companyId || !daysAgo || loading}
				>
					Fetch deleted tasks
				</button>
			</Controls>
			<Controls>
				{data?.pageNumber && data.pageSize && data.totalObjectCount ? (
					<>
						<div>
							{`Showing ${(data.pageNumber - 1) * data.pageSize + 1} to ${Math.min(
								data.pageNumber * data.pageSize,
								data.totalObjectCount
							)} out of ${data.totalObjectCount} deleted tasks.`}
						</div>
						<div>
							<button
								disabled={page < 2 || loading}
								onClick={() => {
									setPage(page - 1);
								}}
							>
								Prev
							</button>
							<button
								disabled={data.pageNumber * data.pageSize >= data.totalObjectCount || loading}
								onClick={() => {
									setPage(page + 1);
								}}
							>
								Next
							</button>
						</div>
					</>
				) : null}
			</Controls>
			<div style={{maxHeight: '85vh', overflowY: 'scroll'}}>
				<table>
					<tbody>
						<tr>
							<th>Task Name</th>
							<th>Project Name</th>
							<th>Deleted at (UTC)</th>
							<th>Deleted by</th>
							<th>Restore</th>
						</tr>
						{data?.pageContents?.map(task => {
							return (
								<tr key={task?.id}>
									<td>{task?.name}</td>
									<td>{task?.projectName}</td>
									<td>{task?.deletedAt}</td>
									<td>{task?.deletedBy}</td>
									<td>
										<button
											onClick={() => {
												restoreTask(task?.id, task?.name || '');
											}}
											disabled={restoring}
										>
											Restore Task
										</button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export const RestoreDataQuery = graphql`
	query RestoreData_Query($companyId: Int, $page: Int, $daysAgo: Int) {
		viewer {
			actualPersonId
			component(name: "restore_data")
			...RestoreData_viewer @arguments(companyId: $companyId, page: $page, daysAgo: $daysAgo)
		}
	}
`;

export default createRefetchContainer(
	RestoreData,
	{
		viewer: graphql`
			fragment RestoreData_viewer on Viewer
			@argumentDefinitions(companyId: {type: "Int"}, page: {type: "Int"}, daysAgo: {type: "Int"}) {
				id
				...MissionControlHeader_viewer
				missionControlData {
					id
					paginatedDeletedTaskList(companyId: $companyId, page: $page, daysAgo: $daysAgo) {
						id
						pageSize
						pageNumber
						totalObjectCount
						pageContents {
							id
							name
							projectName
							deletedAt
							deletedBy
						}
					}
				}
			}
		`,
	},
	graphql`
		query RestoreDataRefetchQuery($companyId: Int, $page: Int, $daysAgo: Int) {
			viewer {
				...RestoreData_viewer @arguments(companyId: $companyId, page: $page, daysAgo: $daysAgo)
			}
		}
	`
);
